import React, { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { compressImage } from "@/lib/image";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Trash2, Upload } from "lucide-react";
import { SectionHeader } from "@/components/admin/common";

export default function AdminGallery() {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [caption, setCaption] = useState("");
  const [file, setFile] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/gallery/photos");
      setPhotos(data);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onUpload = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Please choose a file first.");
    setUploading(true);
    try {
      const compressed = await compressImage(file);
      if (compressed.size > 6 * 1024 * 1024) {
        toast.error("Image is still too large after compression (max 6MB). Try a smaller photo.");
        return;
      }
      const form = new FormData();
      form.append("file", compressed);
      form.append("caption", caption);
      await api.post("/admin/photos", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Photo uploaded!");
      setFile(null);
      setCaption("");
      e.target.reset?.();
      load();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setUploading(false);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this photo? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/photos/${id}`);
      toast.success("Photo deleted");
      setPhotos((p) => p.filter((ph) => ph.id !== id));
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  return (
    <div>
      <SectionHeader
        title="Gallery photos"
        description="Upload photos of your dishes — they'll appear on the public gallery alongside the stock images. JPG, PNG, WebP or GIF, up to 6MB each."
      />

      <form
        onSubmit={onUpload}
        data-testid="admin-upload-form"
        className="rounded-3xl bg-white border border-[#E8DED5] p-6 md:p-8 grid grid-cols-1 md:grid-cols-12 gap-5 items-end"
      >
        <div className="md:col-span-5 space-y-2">
          <Label htmlFor="photo-file">Photo file</Label>
          <Input
            id="photo-file"
            data-testid="admin-file-input"
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="h-11 rounded-xl file:mr-4 file:rounded-md file:border-0 file:bg-[#F5ECE1] file:px-4 file:h-9 file:font-semibold"
          />
        </div>
        <div className="md:col-span-5 space-y-2">
          <Label htmlFor="photo-caption">Caption (optional)</Label>
          <Input
            id="photo-caption"
            data-testid="admin-caption"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder="e.g. Pad Thai Goong — Signature"
            className="h-11 rounded-xl"
          />
        </div>
        <div className="md:col-span-2">
          <Button
            type="submit"
            data-testid="admin-upload-submit"
            disabled={uploading}
            className="w-full h-11 rounded-full bg-[#E53935] hover:bg-[#C62828] text-white font-semibold"
          >
            {uploading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <Upload className="h-4 w-4 mr-2" /> Upload
              </>
            )}
          </Button>
        </div>
      </form>

      <div className="mt-10">
        <h2 className="font-display font-bold text-xl">
          Uploaded photos{" "}
          <span className="text-[#5C534D] font-normal text-base">
            ({photos.length})
          </span>
        </h2>

        {loading ? (
          <div className="mt-8 grid place-items-center h-40">
            <Loader2 className="h-6 w-6 animate-spin text-[#E53935]" />
          </div>
        ) : photos.length === 0 ? (
          <div
            data-testid="admin-empty-state"
            className="mt-6 rounded-2xl border border-dashed border-[#E8DED5] bg-white p-10 text-center text-[#5C534D]"
          >
            No photos uploaded yet. Your uploads will appear on the public Gallery instantly.
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {photos.map((p) => (
              <div
                key={p.id}
                data-testid={`admin-photo-${p.id}`}
                className="group relative rounded-2xl overflow-hidden border border-[#E8DED5] bg-white"
              >
                <div className="aspect-square overflow-hidden bg-[#F5ECE1]">
                  <img
                    src={p.data_url}
                    alt={p.caption}
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="p-3 flex items-center justify-between gap-2">
                  <div className="text-sm font-semibold truncate">
                    {p.caption}
                  </div>
                  <button
                    data-testid={`admin-delete-${p.id}`}
                    onClick={() => onDelete(p.id)}
                    aria-label="Delete photo"
                    className="h-9 w-9 rounded-full bg-[#FAF8F5] hover:bg-[#E53935] hover:text-white grid place-items-center text-[#E53935] border border-[#E8DED5] transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
